import { TO_DO_STATUS } from 'enums';
import { PERSPIRATION_LEVEL } from 'constants/general';
import { PROJECT_STATUS_COLOR_CSS_VAR } from 'constants/project';
import { ProjectDetailsTaskFormData } from 'contexts/ProjectDetailsTaskFormContext';
import { TaskAssignee, ToDoTask } from 'interfaces/common.interface';
import { CreateToDoPayload } from 'interfaces';
import {
  ProjectMemberInvitationStatus,
  ProjectStatus,
  ProjectCustomStatusSection,
  Project
} from 'interfaces/project.interface';
import { DateRangeFilter, PerspirationLevel } from 'interfaces/todos.interface';
import { t } from 'services/i18n.service';
import {
  DEFAULT_TOP_OUTCOME_LEVEL,
  DEFAULT_TOP_PERSPIRATION_LEVEL,
  getDefaultTopMetrics
} from 'constants/topPrioritization';
import { getTomorrowISODate } from 'utils/time.util';
import {
  buildAssigneePayloadFields,
  computeTopScorePreview,
  getOutcomeLabelFromScore,
  getPerspirationLabelFromScore,
  projectUsesCustomStatuses,
  resolveTaskDurationSeconds
} from 'utils/todo.util';
import type { CSSProperties } from 'react';

export const filterTasksByProjectId = (
  tasks: ToDoTask[],
  projectId: string
): ToDoTask[] => {
  if (!tasks.length || !projectId) {
    return [];
  }

  return tasks.filter((task) =>
    (task.tags ?? []).some((tag) => String(tag.id) === String(projectId))
  );
};

export const filterProjectDetailsTasks = ({
  tasks,
  taskName,
  perspirationLevel,
  dueDateRange
}: {
  tasks: ToDoTask[];
  taskName: string;
  perspirationLevel?: PerspirationLevel;
  dueDateRange?: DateRangeFilter | null;
}): ToDoTask[] => {
  const byName = taskName.trim().toLowerCase();

  return tasks.filter((task) => {
    if (byName && !task.title.toLowerCase().includes(byName)) {
      return false;
    }

    if (
      perspirationLevel === PERSPIRATION_LEVEL.LOW &&
      (task.perspiration_level ?? 0) > 5
    ) {
      return false;
    }

    if (
      perspirationLevel === PERSPIRATION_LEVEL.FULL &&
      (task.perspiration_level ?? 0) <= 5
    ) {
      return false;
    }

    if (dueDateRange?.startDate && task.due_date) {
      if (new Date(task.due_date) < new Date(dueDateRange.startDate)) {
        return false;
      }
    }

    if (dueDateRange?.endDate && task.due_date) {
      if (new Date(task.due_date) > new Date(dueDateRange.endDate)) {
        return false;
      }
    }

    return true;
  });
};

export const resolveProjectKanbanColumnId = ({
  task,
  columns
}: {
  task: ToDoTask;
  columns: ProjectStatus[];
}): string | null => {
  if (!columns.length) {
    return null;
  }

  const storedColumn = task.custom_status_id
    ? columns.find((column) => column.id === task.custom_status_id)
    : undefined;
  const isTaskCompleted = task.status === TO_DO_STATUS.COMPLETED;
  const doesStoredColumnCompleteTask =
    storedColumn?.should_complete_task === true;

  // The stored column is only trusted while it agrees with the task's
  // completion state. Completing a task outside this board (mobile, desktop,
  // the to-do list) leaves custom_status_id pointing at the old column, which
  // would otherwise keep a completed task sitting in "To Do" — and vice versa
  // for a task re-opened outside the board while parked in the done column.
  if (storedColumn && doesStoredColumnCompleteTask === isTaskCompleted) {
    return storedColumn.id;
  }

  // Ensure backward compatibility for legacy tasks that only have default status values.
  const nonCompletedColumns = columns.filter(
    (column) => !column.should_complete_task
  );
  const completedColumn =
    columns.find((column) => column.should_complete_task) ??
    columns[columns.length - 1];

  const statusToColumnId: Partial<Record<TO_DO_STATUS, string>> = {
    [TO_DO_STATUS.NOT_STARTED]: nonCompletedColumns[0]?.id ?? columns[0]?.id,
    [TO_DO_STATUS.IN_PROGRESS]:
      nonCompletedColumns[1]?.id ??
      nonCompletedColumns[0]?.id ??
      columns[0]?.id,
    [TO_DO_STATUS.COMPLETED]: completedColumn?.id
  };

  const normalizedStatus = task.status as TO_DO_STATUS;
  if (normalizedStatus && statusToColumnId[normalizedStatus]) {
    return statusToColumnId[normalizedStatus] ?? null;
  }

  return columns[0]?.id ?? null;
};

/** Sets `--project-status-color` for theme-aware `color-mix` Tailwind classes. */
export const getProjectStatusColorVarStyle = (
  color: string
): CSSProperties =>
  ({
    [PROJECT_STATUS_COLOR_CSS_VAR]: color
  }) as CSSProperties;

/** List/Kanban sections for a project with custom statuses (ordered, includes empty columns). */
export const buildProjectCustomStatusSections = (
  tasks: ToDoTask[],
  customStatuses: ProjectStatus[]
): ProjectCustomStatusSection[] => {
  if (!customStatuses.length) {
    return [];
  }

  const orderedStatuses = [...customStatuses].sort((a, b) => a.order - b.order);
  const tasksByColumnId = new Map<string, ToDoTask[]>(
    orderedStatuses.map((status) => [status.id, []])
  );

  tasks.forEach((task) => {
    const columnId = resolveProjectKanbanColumnId({
      task,
      columns: customStatuses
    });
    const targetColumnId =
      columnId && tasksByColumnId.has(columnId)
        ? columnId
        : orderedStatuses[0]?.id;

    if (targetColumnId) {
      tasksByColumnId.get(targetColumnId)?.push(task);
    }
  });

  return orderedStatuses.map((status) => ({
    id: status.id,
    label: status.label,
    color: status.color,
    tasks: tasksByColumnId.get(status.id) ?? []
  }));
};

export const deriveProjectKanbanStatusFromColumn = ({
  targetColumnId,
  customStatuses
}: {
  targetColumnId: string;
  customStatuses: ProjectStatus[];
}): TO_DO_STATUS => {
  const targetIndex = customStatuses.findIndex((s) => s.id === targetColumnId);
  const targetStatus = customStatuses[targetIndex];

  if (targetStatus?.should_complete_task) {
    return TO_DO_STATUS.COMPLETED;
  }
  if (targetIndex === 0) {
    return TO_DO_STATUS.NOT_STARTED;
  }
  return TO_DO_STATUS.IN_PROGRESS;
};

export const buildProjectKanbanMovedTask = ({
  task,
  targetColumnId,
  customStatuses
}: {
  task: ToDoTask;
  targetColumnId: string;
  customStatuses: ProjectStatus[];
}): ToDoTask => {
  if (customStatuses.length > 0) {
    const derivedStatus = deriveProjectKanbanStatusFromColumn({
      targetColumnId,
      customStatuses
    });
    return {
      ...task,
      custom_status_id: targetColumnId,
      status: derivedStatus
    };
  }

  return {
    ...task,
    status: targetColumnId as TO_DO_STATUS,
    custom_status_id: null
  };
};

export const resolveKanbanDropColumnId = ({
  overId,
  columnIds,
  tasks,
  customStatuses
}: {
  overId: string;
  columnIds: string[];
  tasks: ToDoTask[];
  customStatuses: ProjectStatus[];
}): string | null => {
  if (columnIds.includes(overId)) {
    return overId;
  }

  const overTask = tasks.find((task) => task.id === overId);
  if (!overTask) {
    return null;
  }

  if (customStatuses.length > 0) {
    return resolveProjectKanbanColumnId({
      task: overTask,
      columns: customStatuses
    });
  }

  return overTask.status ?? TO_DO_STATUS.NOT_STARTED;
};

export const isProjectKanbanTaskInColumn = ({
  task,
  columnId,
  customStatuses
}: {
  task: ToDoTask;
  columnId: string;
  customStatuses: ProjectStatus[];
}): boolean => {
  if (customStatuses.length > 0) {
    const currentColumnId = resolveProjectKanbanColumnId({
      task,
      columns: customStatuses
    });
    return currentColumnId === columnId;
  }

  return task.status === columnId;
};

const ensureProjectTaskTags = (
  tags: ToDoTask['tags'] | undefined,
  project: Project
) => {
  const labelTags = (tags ?? []).filter(
    (tag) => String(tag.id) !== String(project.id)
  );
  const projectTag = (tags ?? []).find(
    (tag) => String(tag.id) === String(project.id)
  ) ?? { id: project.id, text: project.name };
  return [...labelTags, projectTag];
};

const resolvePersistStatusFields = (
  task: Partial<ToDoTask>,
  project: Project
): Pick<CreateToDoPayload, 'status' | 'custom_status_id'> => {
  if (!projectUsesCustomStatuses(project)) {
    return {
      status: task.status ?? TO_DO_STATUS.NOT_STARTED,
      custom_status_id: null
    };
  }
  const customStatusId = task.custom_status_id ?? null;
  if (!customStatusId) {
    return {
      status: task.status ?? TO_DO_STATUS.NOT_STARTED,
      custom_status_id: null
    };
  }
  return {
    status: deriveProjectKanbanStatusFromColumn({
      targetColumnId: customStatusId,
      customStatuses: project.custom_statuses
    }),
    custom_status_id: customStatusId
  };
};

/** API-safe PUT body for project-scoped task create/update (strips read-only UI fields). */
export const buildProjectDetailsTaskPersistPayload = (
  task: Partial<ToDoTask>,
  project: Project
): CreateToDoPayload => {
  const statusFields = resolvePersistStatusFields(task, project);
  return {
    id: task.id ?? '',
    title: task.title ?? '',
    details: task.details ?? '',
    objective: task.objective,
    due_date: task.due_date ?? new Date().toISOString().slice(0, 10),
    focus_type: task.focus_type,
    eisenhower_quadrant: task.eisenhower_quadrant ?? 1,
    tags: ensureProjectTaskTags(task.tags, project),
    subtasks: task.subtasks,
    duration: resolveTaskDurationSeconds({
      explicitDuration: task.duration,
      perspirationLevel: task.perspiration_level
    }),
    icon: task.icon,
    outcome: task.outcome,
    perspiration_level: task.perspiration_level,
    project_id: project.id,
    assignee_id: task.assignee_id,
    assigned_mcp_token_id: task.assigned_mcp_token_id ?? null,
    external_task_id: task.external_task_id,
    ...statusFields
  };
};

export const getProjectCustomStatusesById = (
  projects: Project[],
  projectId?: string | null
): ProjectStatus[] => {
  if (!projectId) {
    return [];
  }
  return (
    projects.find((project) => project.id === projectId)?.custom_statuses ?? []
  );
};

/** Done column is the status with `should_complete_task` (label-agnostic). */
export const getProjectDoneStatus = (
  customStatuses: ProjectStatus[]
): ProjectStatus | undefined =>
  customStatuses.find((status) => status.should_complete_task === true);

export const getProjectNonDoneStatuses = (
  customStatuses: ProjectStatus[]
): ProjectStatus[] =>
  [...customStatuses]
    .filter((status) => status.should_complete_task !== true)
    .sort((statusA, statusB) => statusA.order - statusB.order);

/** Checkbox toggle: complete → Done column; restore requires an explicit non-Done status id. */
export const buildProjectDetailsTaskStatusToggle = ({
  isCompleted,
  customStatuses,
  restoreStatusId
}: {
  readonly isCompleted: boolean;
  readonly customStatuses: ProjectStatus[];
  readonly restoreStatusId?: string;
}): Pick<ToDoTask, 'status' | 'custom_status_id'> | null => {
  if (customStatuses.length === 0) {
    return {
      status: isCompleted ? TO_DO_STATUS.COMPLETED : TO_DO_STATUS.NOT_STARTED,
      custom_status_id: null
    };
  }

  const doneStatus = getProjectDoneStatus(customStatuses);
  if (!doneStatus) {
    return null;
  }

  if (isCompleted) {
    return {
      status: TO_DO_STATUS.COMPLETED,
      custom_status_id: doneStatus.id
    };
  }

  if (!restoreStatusId) {
    return null;
  }

  const restoreStatus = getProjectNonDoneStatuses(customStatuses).find(
    (status) => status.id === restoreStatusId
  );
  if (!restoreStatus) {
    return null;
  }

  return {
    status: deriveProjectKanbanStatusFromColumn({
      targetColumnId: restoreStatus.id,
      customStatuses
    }),
    custom_status_id: restoreStatus.id
  };
};

/***************************************************/
/********** Project Details Task Form Utils **********/
/***************************************************/

const getDefaultCustomStatusId = (
  customStatuses: ProjectStatus[]
): string | null => {
  if (customStatuses.length === 0) {
    return null;
  }
  const orderedStatuses = [...customStatuses].sort(
    (projectStatusA, projectStatusB) =>
      projectStatusA.order - projectStatusB.order
  );
  return orderedStatuses[0]?.id ?? null;
};

const getDefaultNewTaskTimeline = () => {
  const dueDate = getTomorrowISODate();
  const defaultTopMetrics = getDefaultTopMetrics();
  return {
    due_date: dueDate,
    topScore: computeTopScorePreview(
      defaultTopMetrics.outcome.value,
      defaultTopMetrics.perspiration.value,
      dueDate
    )
  };
};

export const getInitFormData = (
  customStatuses: ProjectStatus[] = []
): ProjectDetailsTaskFormData => ({
  title: '',
  details: '',
  ...getDefaultTopMetrics(),
  timeline: getDefaultNewTaskTimeline(),
  status: TO_DO_STATUS.NOT_STARTED,
  custom_status_id: getDefaultCustomStatusId(customStatuses),
  expandedSection: 'none',
  previousSection: 'none',
  isEditMode: false,
  assignee: null,
  subtasks: []
});

export const mapTaskToFormData = (
  task: ToDoTask,
  customStatuses: ProjectStatus[] = []
): ProjectDetailsTaskFormData => {
  const dueDate = task.due_date?.slice(0, 10) || getTomorrowISODate();
  const outcomeScore = task.outcome ?? DEFAULT_TOP_OUTCOME_LEVEL;
  const perspirationScore =
    task.perspiration_level ?? DEFAULT_TOP_PERSPIRATION_LEVEL;
  return {
    title: task.title ?? '',
    details: task.details ?? '',
    timeline: {
      due_date: dueDate,
      topScore: computeTopScorePreview(outcomeScore, perspirationScore, dueDate)
    },
    outcome: {
      value: outcomeScore,
      label: getOutcomeLabelFromScore(outcomeScore)
    },
    perspiration: {
      value: perspirationScore,
      label: getPerspirationLabelFromScore(perspirationScore)
    },
    status: (task.status as TO_DO_STATUS) ?? TO_DO_STATUS.NOT_STARTED,
    // Same resolution the board uses, so the drawer never highlights a column
    // that contradicts the task's completion state.
    custom_status_id: customStatuses.length
      ? resolveProjectKanbanColumnId({ task, columns: customStatuses })
      : (task.custom_status_id ?? null),
    expandedSection: 'none',
    previousSection: 'none',
    isEditMode: true,
    assignee: task.assignee ?? null,
    subtasks: task.subtasks ?? []
  };
};

/**
 * An assign-only write starts from the task as the server returned it, not
 * from the hydrated form: hydration fills gaps (an empty due date becomes
 * tomorrow), and the API refuses any change but the assignee on a colleague's
 * synced task.
 */
export const buildAssignOnlyTaskPayload = ({
  task,
  assignee,
  initialAssignee
}: {
  task: ToDoTask;
  assignee?: TaskAssignee | null;
  initialAssignee?: TaskAssignee | null;
}): Partial<ToDoTask> => ({
  ...task,
  ...buildAssigneePayloadFields({ assignee, initialAssignee })
});

export const hasFormChanges = (
  initialData: ProjectDetailsTaskFormData | null,
  currentData: ProjectDetailsTaskFormData
) => {
  const getComparableFields = (data: ProjectDetailsTaskFormData) => ({
    title: data.title.trim(),
    details: data.details.trim(),
    timeline: data.timeline,
    outcome: data.outcome,
    perspiration: data.perspiration,
    status: data.status,
    custom_status_id: data.custom_status_id ?? null,
    assignee: data.assignee,
    subtasks: data.subtasks
  });

  if (!initialData) {
    const baseline = getComparableFields(getInitFormData());
    const current = getComparableFields(currentData);
    return JSON.stringify(baseline) !== JSON.stringify(current);
  }

  const initial = getComparableFields(initialData);
  const current = getComparableFields(currentData);
  return JSON.stringify(initial) !== JSON.stringify(current);
};

const isAssignableInvitationStatus = (
  status: ProjectMemberInvitationStatus,
  shouldIncludePending: boolean
): boolean =>
  status === ProjectMemberInvitationStatus.ACCEPTED ||
  (shouldIncludePending && status === ProjectMemberInvitationStatus.PENDING);

export const getAvailableAssignees = (
  members?: {
    id: string;
    user_id?: string;
    email?: string;
    invitation_status: ProjectMemberInvitationStatus;
  }[],
  { shouldIncludePending = true }: { shouldIncludePending?: boolean } = {}
): TaskAssignee[] => {
  const assignees: TaskAssignee[] = [];
  const seen = new Set<string>();
  members?.forEach((member) => {
    if (
      !isAssignableInvitationStatus(
        member.invitation_status,
        shouldIncludePending
      )
    ) {
      return;
    }
    const candidateId = member.user_id || member.id;
    if (candidateId && !seen.has(candidateId)) {
      assignees.push({
        id: candidateId,
        email: member.email,
        name: member.email
      });
      seen.add(candidateId);
    }
  });
  return assignees;
};

export const getAssigneeDisplayName = (
  assignee?: TaskAssignee | null
): string => {
  if (!assignee) {
    return t('todos.unassigned');
  }

  const name = assignee.name?.trim();
  if (name) {
    return name;
  }

  const email = assignee.email?.trim();
  if (email) {
    return email;
  }

  return t('project.unknown_user');
};
